import { DataTable } from '@/components/admin/data-table';
import { AnonymizedBadge, RoleBadge, StatusBadge } from '@/components/admin/status-badge';
import { type AdminUser, admin } from '@/lib/admin-api';
import type { UserRole, UserStatus } from '@/lib/auth-client';
import { routes } from '@/lib/routes';
import { Plus, Search, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

const COUNTRIES = [
  { code: 'PT', name: 'Portugal' },
  { code: 'BR', name: 'Brazil' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'DE', name: 'Germany' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'IE', name: 'Ireland' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' }
].sort((a, b) => a.name.localeCompare(b.name));

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || '');

  // Create user modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({
    email: '',
    username: '',
    password: '',
    country: 'PT',
    role: 'USER' as UserRole,
    emailVerified: true
  });

  const statusFilter = searchParams.get('status') as UserStatus | null;
  const roleFilter = searchParams.get('role') as UserRole | null;
  const anonymizedFilter = searchParams.get('isAnonymized');
  const page = Number(searchParams.get('page')) || 1;

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await admin.users.list({
        query: searchParams.get('query') || undefined,
        status: statusFilter || undefined,
        role: roleFilter || undefined,
        isAnonymized: anonymizedFilter ? anonymizedFilter === 'true' : undefined,
        page
      });
      setUsers(result.users);
      setPagination({
        page: result.pagination.page,
        totalPages: result.pagination.totalPages,
        total: result.pagination.total
      });
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams, statusFilter, roleFilter, anonymizedFilter, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('query', searchQuery);
    } else {
      params.delete('query');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handleFilterChange = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateError(null);
    setCreateForm({
      email: '',
      username: '',
      password: '',
      country: 'PT',
      role: 'USER',
      emailVerified: true
    });
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setCreateError(null);

    try {
      await admin.users.create(createForm);
      handleCloseCreateModal();
      fetchUsers();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  const columns = [
    {
      key: 'user',
      header: t('admin.users.user'),
      render: (user: AdminUser) => (
        <div>
          <Link
            to={routes.admin.user(user.id)}
            className="font-medium text-ink hover:text-blue transition-colors"
          >
            {user.displayName || user.username}
          </Link>
          {user.displayName && <p className="text-sepia/60 text-xs">@{user.username}</p>}
          <p className="text-sepia/70 text-xs">{user.email}</p>
        </div>
      )
    },
    {
      key: 'status',
      header: t('admin.users.status'),
      render: (user: AdminUser) => (
        <div className="flex flex-col gap-1">
          <StatusBadge status={user.status} />
          <div className="flex gap-1">
            <RoleBadge role={user.role} />
            <AnonymizedBadge anonymizedAt={user.anonymizedAt} />
          </div>
        </div>
      )
    },
    {
      key: 'country',
      header: t('admin.users.country'),
      render: (user: AdminUser) => <span className="text-sepia/80">{user.country}</span>
    },
    {
      key: 'stats',
      header: t('admin.users.activity'),
      render: (user: AdminUser) => (
        <div className="text-xs text-sepia/70">
          <p>
            {user._count.sentLetters} sent / {user._count.receivedLetters} received
          </p>
          {user._count.reportsReceived > 0 && (
            <p className="text-red-500">{user._count.reportsReceived} reports</p>
          )}
        </div>
      )
    },
    {
      key: 'createdAt',
      header: t('admin.users.joined'),
      render: (user: AdminUser) => (
        <span className="text-sepia/70 text-xs">
          {new Date(user.createdAt).toLocaleDateString()}
        </span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">{t('admin.users.title')}</h1>
          <p className="text-sepia/70 mt-1">{t('admin.users.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue text-white rounded-lg hover:bg-blue/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('admin.users.createUser')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-warm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sepia/50" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('admin.users.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
              />
            </div>
          </form>

          {/* Status Filter */}
          <select
            value={statusFilter || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
          >
            <option value="">{t('admin.users.allStatuses')}</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="BANNED">Banned</option>
          </select>

          {/* Role Filter */}
          <select
            value={roleFilter || ''}
            onChange={(e) => handleFilterChange('role', e.target.value)}
            className="px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
          >
            <option value="">{t('admin.users.allRoles')}</option>
            <option value="USER">User</option>
            <option value="ADMIN">Admin</option>
          </select>

          {/* Anonymized Filter */}
          <select
            value={anonymizedFilter || ''}
            onChange={(e) => handleFilterChange('isAnonymized', e.target.value)}
            className="px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
          >
            <option value="">{t('admin.users.allUsers')}</option>
            <option value="false">Active accounts</option>
            <option value="true">Anonymized</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <DataTable
        columns={columns}
        data={users}
        keyExtractor={(user) => user.id}
        pagination={{
          page: pagination.page,
          totalPages: pagination.totalPages,
          total: pagination.total,
          onPageChange: handlePageChange
        }}
        emptyMessage={t('admin.users.noUsers')}
        isLoading={isLoading}
      />

      {/* Create User Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={handleCloseCreateModal}
            onKeyDown={(e) => e.key === 'Enter' && handleCloseCreateModal()}
            role="presentation"
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <button
                type="button"
                onClick={handleCloseCreateModal}
                className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white transition-colors"
              >
                <X className="w-5 h-5 text-sepia/50" />
              </button>

              <h3 className="text-lg font-semibold text-ink mb-4">{t('admin.users.createUser')}</h3>

              {createError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {createError}
                </div>
              )}

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-sepia mb-1">
                    {t('admin.users.create.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
                  />
                </div>

                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-sepia mb-1">
                    {t('admin.users.create.username')}
                  </label>
                  <input
                    type="text"
                    id="username"
                    required
                    pattern="^[a-zA-Z0-9_]+$"
                    minLength={3}
                    maxLength={30}
                    value={createForm.username}
                    onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
                  />
                  <p className="mt-1 text-xs text-sepia/70">
                    {t('admin.users.create.usernameHint')}
                  </p>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-sepia mb-1">
                    {t('admin.users.create.password')}
                  </label>
                  <input
                    type="password"
                    id="password"
                    required
                    minLength={8}
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
                  />
                  <p className="mt-1 text-xs text-sepia/70">
                    {t('admin.users.create.passwordHint')}
                  </p>
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-sepia mb-1">
                    {t('admin.users.create.country')}
                  </label>
                  <select
                    id="country"
                    required
                    value={createForm.country}
                    onChange={(e) => setCreateForm({ ...createForm, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-sepia mb-1">
                    {t('admin.users.create.role')}
                  </label>
                  <select
                    id="role"
                    value={createForm.role}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, role: e.target.value as UserRole })
                    }
                    className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
                  >
                    <option value="USER">User</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="emailVerified"
                    checked={createForm.emailVerified}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, emailVerified: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-gray-warm text-blue focus:ring-blue"
                  />
                  <label htmlFor="emailVerified" className="text-sm text-sepia">
                    {t('admin.users.create.emailVerified')}
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseCreateModal}
                    disabled={isCreating}
                    className="px-4 py-2 text-sm font-medium text-sepia bg-white border border-gray-warm rounded-lg hover:bg-cream/50 transition-colors disabled:opacity-50"
                  >
                    {t('admin.common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue rounded-lg hover:bg-blue/90 transition-colors disabled:opacity-50"
                  >
                    {isCreating ? t('admin.users.create.creating') : t('admin.users.create.submit')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
